<script setup>
import {
  ref,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  available: {
    type: Array,
    default: () => [],
  },
  multi: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxVisible: {
    type: Number,
    default: 5,
  },
  showSelected: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();

const comboboxOpen = ref(false);
const searchQuery = ref('');
const activeIndex = ref(-1);
const containerRef = ref(null);
const searchRef = ref(null);
const triggerRef = ref(null);

const componentId = `pipeline-user-picker-${getCurrentInstance()?.uid ?? 0}`;
const listboxId = `${componentId}-listbox`;

// ── Derived ──────────────────────────────────────────────────────────────────

const selectedUsers = computed(() =>
  props.modelValue
    .map(id => props.available.find(u => u.id === id))
    .filter(Boolean)
);

const visibleUsers = computed(() =>
  selectedUsers.value.slice(0, props.maxVisible)
);

const overflowCount = computed(() =>
  Math.max(0, selectedUsers.value.length - props.maxVisible)
);

const filteredAvailable = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return props.available.filter(u => {
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  });
});

const effectivePlaceholder = computed(
  () => props.placeholder || t('PIPELINE.INLINE_EDIT.ADD_AGENT')
);

const activeOptionId = computed(() => {
  const user = filteredAvailable.value[activeIndex.value];
  return user ? `${componentId}-option-${user.id}` : undefined;
});

// ── Interactions ─────────────────────────────────────────────────────────────

const setInitialActiveIndex = position => {
  if (!filteredAvailable.value.length) {
    activeIndex.value = -1;
    return;
  }

  if (position === 'last') {
    activeIndex.value = filteredAvailable.value.length - 1;
    return;
  }

  const selectedIndex = filteredAvailable.value.findIndex(user =>
    props.modelValue.includes(user.id)
  );
  activeIndex.value = selectedIndex >= 0 ? selectedIndex : 0;
};

const scrollActiveOptionIntoView = () => {
  nextTick(() => {
    if (!activeOptionId.value) return;
    document
      .getElementById(activeOptionId.value)
      ?.scrollIntoView?.({ block: 'nearest' });
  });
};

const openCombobox = (position = 'selected') => {
  if (props.disabled) return;
  searchQuery.value = '';
  comboboxOpen.value = true;
  nextTick(() => {
    setInitialActiveIndex(position);
    searchRef.value?.focus();
    scrollActiveOptionIntoView();
  });
};

const closeCombobox = ({ restoreFocus = false } = {}) => {
  comboboxOpen.value = false;
  searchQuery.value = '';
  activeIndex.value = -1;
  if (restoreFocus) nextTick(() => triggerRef.value?.focus());
};

const removeUser = userId => {
  if (props.disabled) return;
  const prev = [...props.modelValue];
  const next = prev.filter(id => id !== userId);
  emit('update:modelValue', next);
  emit('change', next, prev);
};

const addUser = user => {
  const prev = [...props.modelValue];
  let next;

  if (!props.multi) {
    next = [user.id];
  } else if (prev.includes(user.id)) {
    next = prev.filter(id => id !== user.id);
  } else {
    next = [...prev, user.id];
  }

  emit('update:modelValue', next);
  emit('change', next, prev);

  if (!props.multi) closeCombobox({ restoreFocus: true });
  else nextTick(() => searchRef.value?.focus());
};

const isSelected = userId => props.modelValue.includes(userId);

const moveActiveIndex = direction => {
  const count = filteredAvailable.value.length;
  if (!count) return;

  if (direction === 'first') activeIndex.value = 0;
  else if (direction === 'last') activeIndex.value = count - 1;
  else if (direction === 'next') {
    activeIndex.value = (activeIndex.value + 1 + count) % count;
  } else {
    activeIndex.value = (activeIndex.value - 1 + count) % count;
  }
  scrollActiveOptionIntoView();
};

const onTriggerKeydown = event => {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  openCombobox(
    event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first'
  );
};

const onComboboxKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeCombobox({ restoreFocus: true });
    return;
  }

  if (event.key === 'Tab') {
    closeCombobox();
    return;
  }

  const navigation = {
    ArrowDown: 'next',
    ArrowUp: 'previous',
    Home: 'first',
    End: 'last',
  }[event.key];
  if (navigation) {
    event.preventDefault();
    event.stopPropagation();
    moveActiveIndex(navigation);
    return;
  }

  if (event.key === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault();
    event.stopPropagation();
    addUser(filteredAvailable.value[activeIndex.value]);
  }
};

// Kept as a public alias for callers and existing tests.
const onKeydown = onComboboxKeydown;

// ── Click-outside ────────────────────────────────────────────────────────────

const onDocumentClick = e => {
  if (!comboboxOpen.value) return;
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    closeCombobox();
  }
};

watch(filteredAvailable, users => {
  if (!comboboxOpen.value) return;
  activeIndex.value = users.length ? 0 : -1;
});

watch(
  () => props.disabled,
  disabled => {
    if (disabled) closeCombobox();
  }
);

onMounted(() => document.addEventListener('click', onDocumentClick, true));
onBeforeUnmount(() =>
  document.removeEventListener('click', onDocumentClick, true)
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const userStatus = user => {
  if (user.online === true) return 'online';
  return null;
};

// Expose for testing
defineExpose({
  activeIndex,
  comboboxOpen,
  onKeydown,
  closeCombobox,
  openCombobox,
});
</script>

<template>
  <div
    ref="containerRef"
    class="relative inline-flex flex-wrap items-center gap-2"
  >
    <!-- ── Selected users list ── -->
    <template v-if="showSelected">
      <div
        v-for="user in visibleUsers"
        :key="user.id"
        class="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-n-alpha-1 pl-2"
      >
        <Avatar
          :name="user.name"
          :src="user.avatar_url"
          :size="22"
          rounded-full
          :status="userStatus(user)"
        />
        <span
          class="max-w-[100px] truncate text-xs font-medium text-n-slate-12"
        >
          {{ user.name }}
        </span>
        <button
          v-if="!disabled"
          type="button"
          :aria-label="
            t('PIPELINE.INLINE_EDIT.REMOVE_AGENT', { agent: user.name })
          "
          :title="t('PIPELINE.INLINE_EDIT.REMOVE_AGENT', { agent: user.name })"
          class="inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-full text-n-slate-10 transition-colors duration-75 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click.stop="removeUser(user.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <!-- Overflow badge -->
      <span
        v-if="overflowCount > 0"
        class="inline-flex h-11 min-w-11 items-center justify-center rounded-full bg-n-alpha-2 px-2 text-xs font-medium text-n-slate-11"
      >
        {{ t('PIPELINE.INLINE_EDIT.OVERFLOW_COUNT', { count: overflowCount }) }}
      </span>

      <!-- Empty state label -->
      <span
        v-if="selectedUsers.length === 0 && !comboboxOpen"
        class="text-xs italic text-n-slate-10"
      >
        {{ t('PIPELINE.INLINE_EDIT.EMPTY_LIST') }}
      </span>
    </template>

    <!-- ── Add button ── -->
    <button
      v-if="!disabled"
      ref="triggerRef"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="comboboxOpen"
      :aria-controls="listboxId"
      :aria-label="effectivePlaceholder"
      class="inline-flex min-h-11 items-center gap-1 rounded-full border border-dashed border-n-weak px-3 py-2 text-xs text-n-slate-11 transition-colors duration-100 hover:bg-n-alpha-1 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
      @click.stop="openCombobox()"
      @keydown="onTriggerKeydown"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      {{ effectivePlaceholder }}
    </button>

    <!-- ── Combobox dropdown ── -->
    <div
      v-if="comboboxOpen"
      class="absolute left-0 top-full z-50 mt-1 min-w-[280px] rounded-lg border border-n-weak bg-n-solid-1 p-2 shadow-lg"
    >
      <!-- Search input -->
      <div class="mb-2">
        <input
          ref="searchRef"
          v-model="searchQuery"
          type="search"
          role="combobox"
          name="pipeline-agent-search"
          autocomplete="off"
          aria-autocomplete="list"
          :aria-label="t('PIPELINE.INLINE_EDIT.SEARCH_USERS')"
          :aria-expanded="comboboxOpen"
          :aria-controls="listboxId"
          :aria-activedescendant="activeOptionId"
          :placeholder="t('PIPELINE.INLINE_EDIT.SEARCH_USERS')"
          class="min-h-11 w-full rounded-md border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @keydown="onComboboxKeydown"
        />
      </div>

      <!-- Results list -->
      <div
        :id="listboxId"
        role="listbox"
        :aria-label="t('PIPELINE.INLINE_EDIT.SEARCH_USERS')"
        :aria-multiselectable="multi"
        class="max-h-[248px] overflow-y-auto overscroll-contain"
      >
        <button
          v-for="(user, index) in filteredAvailable"
          :id="`${componentId}-option-${user.id}`"
          :key="user.id"
          role="option"
          type="button"
          tabindex="-1"
          :aria-selected="isSelected(user.id)"
          class="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded px-2 py-2 text-left transition-colors duration-75 hover:bg-n-alpha-1"
          :class="
            index === activeIndex || isSelected(user.id) ? 'bg-n-alpha-1' : ''
          "
          @mousedown.prevent
          @mousemove="activeIndex = index"
          @click.stop="addUser(user)"
        >
          <Avatar
            :name="user.name"
            :src="user.avatar_url"
            :size="28"
            rounded-full
            :status="userStatus(user)"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ user.name }}
            </p>
            <p v-if="user.email" class="text-xs text-n-slate-10 truncate">
              {{ user.email }}
            </p>
          </div>

          <!-- Selected checkmark -->
          <svg
            v-if="isSelected(user.id)"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-n-brand flex-shrink-0"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>

        <!-- No results -->
        <p
          v-if="filteredAvailable.length === 0"
          role="status"
          class="py-3 text-center text-xs text-n-slate-10"
        >
          {{ t('PIPELINE.INLINE_EDIT.NO_USERS_FOUND') }}
        </p>
      </div>
    </div>
  </div>
</template>
